import { useMemo, useCallback } from 'react';
import { useGlobalCompany } from './useGlobalCompany';
import { usePermissions } from './usePermissions';
import { PaymentType } from '@/types';

const PAYMENT_TYPE_FEATURE_KEY: Record<PaymentType, string> = {
    Cash: 'payments.cash',
    CreditCard: 'payments.credit-card',
    Check: 'payments.check',
    ACHWire: 'payments.ach-wire',
    ZelleVenmo: 'payments.zelle-venmo',
};
interface JobEditPermissions {
    /** Whether the user can edit jobs at all (jobs.edit permission) */
    canEditJobs: boolean;
    /** Check if a specific job edit sub-field is allowed (e.g. canEditField('date') checks jobs.edit.date) */
    canEditField: (field: string) => boolean;
    /** Filter a list of PaymentType values to only those the user is allowed to record */
    allowedPaymentTypes: (allTypes: PaymentType[]) => PaymentType[];
    /** Whether the user can edit completed jobs (jobs.edit.completed) */
    canEditCompletedJobs: boolean;
    /** Whether the user can view payout fields (jobs.view.payout) */
    canViewPayoutFields: boolean;
    /** Whether the user can use Tap to Pay (payments.tap-to-pay) */
    canUseTapToPay: boolean;
    /** Whether the permissions data is still loading */
    isLoading: boolean;
}

/**
 * Job-edit and job-view sub-permissions for the current user.
 *
 * Reads the single feature map already held by PermissionsProvider rather than fetching its
 * own. `getMyFeatures('')` returns *every* key (see FeatureController.GetCurrentUserFeatures:
 * "Empty prefix -> all keys"), so the "jobs.edit."/"jobs.view."/"payments." fetches this hook
 * used to make were re-downloading a subset of a map the app already had.
 *
 * That mattered: those three query keys were not in the persister allowlist, so on a cold start
 * they began at `undefined` while the job itself rehydrated from IndexedDB and rendered. Since
 * "not loaded yet" reads as "denied" here, a stalled connection left the job details screen
 * silently read-only for users who do hold jobs.edit. The shared `user-features` query is
 * persisted, uses keepPreviousData and retries 3x, so it survives all three cases.
 *
 * Readiness/legacy-204 semantics live in usePermissions — see the comments there.
 */
export function useJobEditPermissions(): JobEditPermissions {
    const { selectedCompanyId } = useGlobalCompany();
    const { isFeatureAllowed, isLoading, permissions } = usePermissions();

    const canEditJobs = isFeatureAllowed('jobs.edit');

    const canEditField = useCallback((field: string): boolean => {
        // If the parent permission is denied, all sub-fields are denied
        if (!isFeatureAllowed('jobs.edit')) return false;
        return isFeatureAllowed(`jobs.edit.${field}`);
    }, [isFeatureAllowed]);

    const canEditCompletedJobs = isFeatureAllowed('jobs.edit.completed');

    // Defaults to allowed when the key is absent (unlike isFeatureAllowed), so read the raw map.
    const canViewPayoutFields = useMemo(() => {
        if (isLoading) return false;
        return permissions.get('jobs.view.payout') ?? true;
    }, [isLoading, permissions]);

    const allowedPaymentTypes = useCallback((allTypes: PaymentType[]): PaymentType[] => {
        if (isLoading) return allTypes;
        return allTypes.filter(type => {
            const featureKey = PAYMENT_TYPE_FEATURE_KEY[type];
            if (!featureKey) return true;
            return permissions.get(featureKey) ?? false;
        });
    }, [isLoading, permissions]);

    const canUseTapToPay = useMemo(() => {
        // Default closed when no company is selected. Apple 1.3 restricts who may
        // accept Tap to Pay terms to admins/authorized parties, so an unresolved
        // permission must not fall open — the checkout button then shows the
        // "ask an admin" message rather than silently enabling.
        //
        // The legacy-204 allow-all deliberately does NOT apply here: against a rolled-back
        // backend this denies admins Tap to Pay where the old build allowed it, which is the
        // right way round for a store-compliance gate — the cost is an "ask an admin" message,
        // not a violation. Reading the raw map preserves that: it is empty in the 204 case.
        if (!selectedCompanyId || isLoading) return false;
        return permissions.get('payments.tap-to-pay') ?? false;
    }, [selectedCompanyId, isLoading, permissions]);

    return {
        canEditJobs,
        canEditField,
        allowedPaymentTypes,
        canEditCompletedJobs,
        canViewPayoutFields,
        canUseTapToPay,
        isLoading,
    };
}
